import { useCallback, useEffect, useState } from 'react';
import { downloadVideo, fetchVideoBlobUrl } from '../api';
import { useBlobUrl } from '../hooks/useBlobUrl';
import type { Job } from '../types';
import { VideoDebugModal } from './VideoDebugModal';
import { Viewer360 } from './Viewer360';

export type PlayerMode = 'video' | '360';

interface PlayerModalProps {
  job: Job | null;
  initialMode?: PlayerMode;
  onClose: () => void;
}

interface TabSwitchProps {
  mode: PlayerMode;
  onChange: (m: PlayerMode) => void;
}

function TabSwitch({ mode, onChange }: TabSwitchProps) {
  const baseBtn =
    'px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md border border-slate-200';
  const active = 'bg-indigo-50 text-indigo-700 border-indigo-300';
  const inactive = 'bg-white text-slate-600 hover:bg-slate-50';
  return (
    <div className="inline-flex" role="tablist" aria-label="Viewer mode">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'video'}
        onClick={() => onChange('video')}
        className={`${baseBtn} ${mode === 'video' ? active : inactive} -mr-px`}
      >
        Video
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === '360'}
        onClick={() => onChange('360')}
        className={`${baseBtn} ${mode === '360' ? active : inactive}`}
      >
        360
      </button>
    </div>
  );
}

export function PlayerModal({ job, initialMode = 'video', onClose }: PlayerModalProps) {
  const [mode, setMode] = useState<PlayerMode>(initialMode);
  const [downloading, setDownloading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (job) setMode(initialMode);
  }, [job, initialMode]);

  useEffect(() => {
    if (!job) return;
    setShowDebug(false);
  }, [job]);

  useEffect(() => {
    if (!job) return;
    const onKey = (e: KeyboardEvent) => {
      // The debug panel stacks on top and handles its own Escape.
      if (e.key === 'Escape' && !showDebug) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [job, onClose, showDebug]);

  const videoFetcher = useCallback(
    (signal: AbortSignal) => fetchVideoBlobUrl(job?.job_id ?? '', signal),
    [job?.job_id],
  );
  const videoSrc = useBlobUrl(job ? videoFetcher : null);

  const handleDownload = useCallback(async () => {
    if (!job) return;
    setDownloading(true);
    try {
      await downloadVideo(job.job_id, job.scan_id);
    } finally {
      setDownloading(false);
    }
  }, [job]);

  if (!job) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-400">Scan</div>
            <div className="text-sm font-mono text-slate-900 truncate">{job.scan_id}</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <TabSwitch mode={mode} onChange={setMode} />
            <button
              type="button"
              onClick={() => setShowDebug(true)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Debug panel
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-500 shadow hover:shadow-md hover:opacity-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {downloading ? 'Downloading…' : 'Download'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {mode === 'video' ? (
          <div className="bg-black relative">
            <video
              key={job.job_id}
              {...(videoSrc ? { src: videoSrc } : {})}
              controls
              autoPlay
              className="w-full max-h-[75vh] mx-auto block"
            />
            {!videoSrc && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm text-white text-sm">
                Loading video…
              </div>
            )}
          </div>
        ) : (
          <Viewer360 key={job.job_id} jobId={job.job_id} scanId={job.scan_id} />
        )}
      </div>

      <VideoDebugModal job={job} isOpen={showDebug} onClose={() => setShowDebug(false)} />
    </div>
  );
}
